function normalizeAddress(value) {
  const raw = String(value || '').trim().toLowerCase();
  return /^0x[0-9a-f]{40}$/.test(raw) ? raw : '';
}

function walkAddresses(input, collector) {
  if (!input) return;
  if (typeof input === 'string') {
    const matches = input.match(/0x[0-9a-fA-F]{40}/g) || [];
    matches.forEach((value) => collector.add(String(value).toLowerCase()));
    return;
  }
  if (Array.isArray(input)) {
    input.forEach((value) => walkAddresses(value, collector));
    return;
  }
  if (typeof input === 'object') {
    Object.keys(input).forEach((key) => walkAddresses(input[key], collector));
  }
}

function getDateMs(value) {
  const ts = new Date(String(value || '').trim()).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

function recencyBoost(value) {
  const ts = getDateMs(value);
  if (!ts) return 0;
  const ageHours = Math.max(0, (Date.now() - ts) / (1000 * 60 * 60));
  if (ageHours <= 24) return 1.5;
  if (ageHours <= 24 * 7) return 0.75;
  return 0;
}

function uniqueStrings(values, limit = 3) {
  const seen = new Set();
  const out = [];
  (Array.isArray(values) ? values : []).forEach((value) => {
    const v = String(value || '').trim();
    const key = v.toLowerCase();
    if (!v || seen.has(key)) return;
    seen.add(key);
    out.push(v);
  });
  return out.slice(0, limit);
}

function addCandidate(map, candidate, kind, weight, reason, extra = {}) {
  const address = normalizeAddress(candidate);
  if (!address) return;
  const current = map.get(address) || {
    address,
    kind,
    score: 0,
    reasons: [],
    mentionCount: 0,
    directCount: 0,
    latestTs: 0,
  };
  current.kind = kind || current.kind;
  current.score += Number(weight) || 0;
  current.mentionCount += 1;
  if ((Number(weight) || 0) >= 2.5) current.directCount += 1;
  if (reason) current.reasons.push(reason);
  if (extra.ts) current.latestTs = Math.max(current.latestTs || 0, getDateMs(extra.ts));
  map.set(address, current);
}

function scoreAndSort(rows) {
  return [...rows.values()]
    .map((row) => ({
      ...row,
      reasons: uniqueStrings(row.reasons, 3),
      score: Number(row.score.toFixed(2)),
    }))
    .sort((a, b) => b.score - a.score || b.directCount - a.directCount || b.latestTs - a.latestTs || a.address.localeCompare(b.address));
}

export function deriveCounterpartyInsights({ address, stories, theses, liquidity }) {
  const tokenAddress = normalizeAddress(address);
  const storyRows = Array.isArray(stories && stories.stories) ? stories.stories : [];
  const thesisRows = Array.isArray(theses && theses.theses) ? theses.theses : [];
  const walletMap = new Map();
  const tokenMap = new Map();
  const poolMap = new Map();

  storyRows.forEach((story) => {
    const ts = story && (story.ts_created || story.ts_updated);
    const score = Number(story && story.score);
    const scoreBoost = Number.isFinite(score) ? score * 2 : 0;
    const freshness = recencyBoost(ts);
    const meta = story && story.meta && typeof story.meta === 'object' ? story.meta : {};

    [meta.wallet, meta.address, meta.from, meta.to, meta.owner, meta.trader].forEach((candidate) => {
      const addr = normalizeAddress(candidate);
      if (!addr || addr === tokenAddress) return;
      addCandidate(walletMap, addr, 'wallet', 1 + scoreBoost + freshness, 'Direct story mention', { ts });
    });

    [story && story.primary_token, meta.token, meta.token_address, meta.contract].forEach((candidate) => {
      const addr = normalizeAddress(candidate);
      if (!addr || addr === tokenAddress) return;
      addCandidate(tokenMap, addr, 'token', 1.5 + scoreBoost + freshness, 'Token co-mention', { ts });
    });

    const discovered = new Set();
    walkAddresses(meta, discovered);
    walkAddresses(story && story.subtitle, discovered);
    discovered.forEach((candidate) => {
      const addr = normalizeAddress(candidate);
      if (!addr || addr === tokenAddress) return;
      if (tokenMap.has(addr)) {
        addCandidate(tokenMap, addr, 'token', 0.75 + freshness, 'Repeated narrative co-mention', { ts });
      } else {
        addCandidate(walletMap, addr, 'wallet', 0.75 + freshness, 'Narrative evidence', { ts });
      }
    });
  });

  thesisRows.forEach((thesis) => {
    const ts = thesis && (thesis.ts_updated || thesis.ts_created);
    const conviction = Number(thesis && thesis.conviction);
    const convictionBoost = Number.isFinite(conviction) ? conviction * 2.5 : 0;
    const freshness = recencyBoost(ts);
    const entityAddress = normalizeAddress(thesis && thesis.entity_address);
    if (entityAddress && entityAddress !== tokenAddress) {
      addCandidate(tokenMap, entityAddress, 'token', 2 + convictionBoost + freshness, 'Thesis evidence', { ts });
    }
    [thesis && thesis.thesis_text, thesis && thesis.entry_signal, thesis && thesis.invalidation_signal].forEach((field) => {
      const matches = String(field || '').match(/0x[0-9a-fA-F]{40}/g) || [];
      matches.forEach((candidate) => {
        const addr = normalizeAddress(candidate);
        if (!addr || addr === tokenAddress) return;
        if (entityAddress && addr === entityAddress) {
          addCandidate(tokenMap, addr, 'token', 0.75 + freshness, 'Reinforced thesis target', { ts });
        } else {
          addCandidate(walletMap, addr, 'wallet', 1 + convictionBoost + freshness, 'Thesis evidence', { ts });
        }
      });
    });
  });

  const poolStats = liquidity && liquidity.pool_stats && typeof liquidity.pool_stats === 'object' ? liquidity.pool_stats : null;
  const latest = liquidity && liquidity.latest && typeof liquidity.latest === 'object' ? liquidity.latest : null;
  const poolAddress = normalizeAddress(poolStats && poolStats.pool) || normalizeAddress(latest && latest.price_source_pool);
  const quoteToken = normalizeAddress(poolStats && poolStats.quote_token);
  const poolTs = (poolStats && poolStats.last_swap_ts) || (latest && latest.updated_at) || '';

  if (poolAddress) {
    addCandidate(poolMap, poolAddress, 'pool', 3 + recencyBoost(poolTs), 'Pool source', { ts: poolTs });
  }
  if (quoteToken && quoteToken !== tokenAddress) {
    addCandidate(tokenMap, quoteToken, 'token', 2 + recencyBoost(poolTs), 'Pool quote token', { ts: poolTs });
  }

  const wallets = scoreAndSort(walletMap).slice(0, 4);
  const tokens = scoreAndSort(tokenMap).slice(0, 4);
  const pools = scoreAndSort(poolMap).slice(0, 2);

  return {
    wallets,
    tokens,
    pools,
    summary: {
      walletCount: wallets.length,
      tokenCount: tokens.length,
      poolCount: pools.length,
    },
  };
}

export function shortenCounterpartyLabel(value) {
  const raw = String(value || '').trim();
  if (!/^0x[0-9a-fA-F]{40}$/.test(raw)) return raw || 'Unknown';
  return `${raw.slice(0, 6)}...${raw.slice(-4)}`;
}
